import React from 'react';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import { useTranslation } from 'react-i18next';

import { EditorFocus } from '../EditorMode/editorCategories';
import { useCanUseMapEditor } from '../EditorMode/useCanUseMapEditor';
import { useSpawnableObjects } from '../EditorMode/useSpawnableObjects';

import { useStyles } from './styles';

export interface IEditAreaPickerModalProps {
  open: boolean;
  brandingName?: string;
  // Objects are only brandable when the build declares entities and the map
  // feature flag is on, so the tile stays under "coming soon" otherwise.
  isObjectsAvailable?: boolean;
  // Optional: when the picker was reached from the branding list, going back
  // reopens that list instead of dropping the user out of the flow.
  onBack?: () => void;
  onClose: () => void;
  onPick: (focus: EditorFocus) => void;
}

interface FocusOption {
  id: EditorFocus;
  labelKey: string;
  labelFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
  icon: React.ReactNode;
}

const FOCUS_OPTIONS: FocusOption[] = [
  {
    id: 'environment',
    labelKey: 'editorMode.focusEnvironment',
    labelFallback: 'Map & colors',
    descriptionKey: 'editorMode.focusEnvironmentDesc',
    descriptionFallback: 'Theme, palette and environment art',
    icon: <PaletteOutlinedIcon />,
  },
  {
    id: 'spots',
    labelKey: 'editorMode.focusSpots',
    labelFallback: 'Spots & media',
    descriptionKey: 'editorMode.focusSpotsDesc',
    descriptionFallback: 'Images, videos and QR placements',
    icon: <ImageOutlinedIcon />,
  },
  {
    id: 'hotspots',
    labelKey: 'editorMode.focusHotspots',
    labelFallback: 'Hotspots',
    descriptionKey: 'editorMode.focusHotspotsDesc',
    descriptionFallback: 'Interactive 2D panels & 3D objects',
    icon: <PlaceOutlinedIcon />,
  },
  {
    id: 'spawnables',
    labelKey: 'editorMode.focusSpawnables',
    labelFallback: 'Spawned objects',
    descriptionKey: 'editorMode.focusSpawnablesDesc',
    descriptionFallback: 'Live-spawned engine props',
    icon: <ViewInArOutlinedIcon />,
  },
  {
    id: 'assistant',
    labelKey: 'editorMode.focusAssistant',
    labelFallback: 'AI scripts & context',
    descriptionKey: 'editorMode.focusAssistantDesc',
    descriptionFallback: 'Guided tours and ask-me-anything',
    icon: <SmartToyOutlinedIcon />,
  },
  {
    id: 'mapEditor',
    labelKey: 'editorMode.focusMapEditor',
    labelFallback: 'Map editor',
    descriptionKey: 'editorMode.focusMapEditorDesc',
    descriptionFallback: 'Build the floor plan from room elements',
    icon: <ViewQuiltOutlinedIcon />,
  },
];

const OBJECTS_OPTION: FocusOption = {
  id: 'objects',
  labelKey: 'editorMode.focusObjects',
  labelFallback: 'Objects',
  descriptionKey: 'editorMode.focusObjectsDesc',
  descriptionFallback: 'Place and theme 3D props',
  icon: <ExtensionOutlinedIcon />,
};

interface ComingSoonOption {
  labelKey: string;
  labelFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
  icon: React.ReactNode;
}

const COMING_SOON_OPTIONS: ComingSoonOption[] = [
  {
    labelKey: 'editorMode.focusZones',
    labelFallback: 'Zones',
    descriptionKey: 'editorMode.focusZonesDesc',
    descriptionFallback: 'Define triggers and play areas',
    icon: <MapOutlinedIcon />,
  },
];

const EditAreaPickerModal = ({
  open,
  brandingName,
  isObjectsAvailable = false,
  onBack,
  onClose,
  onPick,
}: IEditAreaPickerModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const canUseMapEditor = useCanUseMapEditor();
  const { available: spawnableObjectsAvailable } = useSpawnableObjects();

  // Three independent gates on the same list: the map editor tile is absent on
  // a map with no room kit, Spawnables on a build that published no schema, and
  // the Objects tile only appears once the build declares entities (otherwise
  // it stays under "coming soon" below). Spawnables is hidden rather than
  // deferred, matching its rail tab — the feature exists, this build just says
  // nothing about what it can place.
  const gatedFocusOptions = FOCUS_OPTIONS.filter((option) => {
    if (option.id === 'mapEditor') {
      return canUseMapEditor;
    }

    if (option.id === 'spawnables') {
      return spawnableObjectsAvailable;
    }

    return true;
  });

  const focusOptions = isObjectsAvailable
    ? [...gatedFocusOptions, OBJECTS_OPTION]
    : gatedFocusOptions;
  const comingSoonOptions = isObjectsAvailable
    ? COMING_SOON_OPTIONS
    : [
        {
          labelKey: OBJECTS_OPTION.labelKey,
          labelFallback: OBJECTS_OPTION.labelFallback,
          descriptionKey: OBJECTS_OPTION.descriptionKey,
          descriptionFallback: OBJECTS_OPTION.descriptionFallback,
          icon: OBJECTS_OPTION.icon,
        },
        ...COMING_SOON_OPTIONS,
      ];

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      sx={classes.modalRoot}
    >
      <Box sx={classes.editAreaContainer}>
        <Box sx={classes.editAreaHeader}>
          <Box sx={classes.editAreaHeaderLeft}>
            <Typography sx={classes.editAreaTitle}>
              {translate(
                'editorMode.editAreaTitle',
                'What do you want to edit?',
              )}
            </Typography>
            {brandingName ? (
              <Typography sx={classes.editAreaSubtitle}>
                {translate('editorMode.editAreaSubtitle', 'Editing {{name}}', {
                  name: brandingName,
                })}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} sx={classes.closeButton} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={classes.editAreaGrid}>
          {focusOptions.map((option) => (
            <Box
              key={option.id}
              sx={classes.editAreaTile}
              onClick={() => onPick(option.id)}
              data-testid={`branding-area-picker-${option.id}-btn`}
            >
              <Box sx={classes.editAreaTileIcon}>{option.icon}</Box>
              <Typography sx={classes.editAreaTileTitle}>
                {translate(option.labelKey, option.labelFallback)}
              </Typography>
              <Typography sx={classes.editAreaTileDescription}>
                {translate(option.descriptionKey, option.descriptionFallback)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={classes.editAreaComingSoonSection}>
          <Typography sx={classes.editAreaComingSoonHeader}>
            {translate('editorMode.comingSoonHeader', 'Coming soon')}
          </Typography>
          <Box sx={classes.editAreaComingSoonGrid}>
            {comingSoonOptions.map((option) => (
              <Box key={option.labelFallback} sx={classes.editAreaTileDisabled}>
                <Box sx={classes.editAreaTileIconDisabled}>{option.icon}</Box>
                <Box sx={classes.editAreaTileDisabledBody}>
                  <Typography sx={classes.editAreaTileTitleDisabled}>
                    {translate(option.labelKey, option.labelFallback)}
                  </Typography>
                  <Typography sx={classes.editAreaTileDescriptionDisabled}>
                    {translate(
                      option.descriptionKey,
                      option.descriptionFallback,
                    )}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={classes.editAreaFooter}>
          {onBack ? (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={classes.cancelButton}
              startIcon={<ArrowBackIcon fontSize="small" />}
              fullWidth
              data-testid="branding-area-picker-back-btn"
            >
              {translate('editorMode.back', 'Back')}
            </Button>
          ) : null}
          <Button
            variant="outlined"
            onClick={onClose}
            sx={classes.cancelButton}
            fullWidth
            data-testid="branding-area-picker-cancel-btn"
          >
            {translate('common.cancel', 'Cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditAreaPickerModal;
